import React, { useState, useEffect, useRef, useCallback } from "react";
import { api } from "../api";
import { SyncListenChatMessageJSON } from "../../../src/apidefs";
import { useUserStatus } from "../state";
import {
  ChatBubbleLeftRightIcon,
  PaperAirplaneIcon,
} from "@heroicons/react/24/outline";
import { RLink } from "../DesignSystem/RLink";
import { Button } from "../DesignSystem/Button";

type SyncListenChatProps = {
  compoId: number;
  syncedToDiscordChannel: boolean;
  isAdmin: boolean;
  onSetDiscordSync: () => void;
};

function formatTime(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function MessageAvatar({ message }: { message: SyncListenChatMessageJSON }) {
  if (message.messageType === "server") {
    return (
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-200 dark:bg-slate-700">
        <ChatBubbleLeftRightIcon className="h-4 w-4 text-slate-500 dark:text-slate-400" />
      </div>
    );
  }

  const avatarUrl = message.avatarUrl;
  const username = message.username || message.discordUsername || "?";

  if (avatarUrl) {
    return (
      <img
        src={avatarUrl}
        alt={username}
        className="h-8 w-8 shrink-0 rounded-full object-cover"
      />
    );
  }

  return (
    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-200 dark:bg-slate-700">
      <span className="text-sm font-medium text-slate-600 dark:text-slate-300">
        {username.charAt(0).toUpperCase()}
      </span>
    </div>
  );
}

function ChatMessage({ message }: { message: SyncListenChatMessageJSON }) {
  if (message.messageType === "server") {
    return (
      <div className="flex items-start gap-2 px-3 py-2">
        <MessageAvatar message={message} />
        <div className="min-w-0 flex-1">
          <div className="text-sm text-slate-500 dark:text-slate-400">
            {message.content || getServerMessageText(message)}
          </div>
          <div className="text-xs text-slate-400 dark:text-slate-500">
            {formatTime(message.createdAt)}
          </div>
        </div>
      </div>
    );
  }

  const username = message.username || message.discordUsername || "Unknown";
  const isDiscord = message.messageType === "discord";

  return (
    <div className="flex items-start gap-2 px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-700/50">
      <MessageAvatar message={message} />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          {message.userId ? (
            <RLink
              to={`/user/${username}`}
              className="text-sm font-medium text-slate-800 hover:underline dark:text-white"
            >
              {username}
            </RLink>
          ) : (
            <span className="text-sm font-medium text-slate-800 dark:text-white">
              {username}
              {isDiscord && (
                <span className="ml-1 text-xs text-slate-400 dark:text-slate-500">
                  (Discord)
                </span>
              )}
            </span>
          )}
          <span className="text-xs text-slate-400 dark:text-slate-500">
            {formatTime(message.createdAt)}
          </span>
        </div>
        <div className="break-words text-sm text-slate-700 dark:text-slate-300">
          {message.content}
        </div>
      </div>
    </div>
  );
}

function getServerMessageText(message: SyncListenChatMessageJSON): string {
  switch (message.serverMessageType) {
    case "sync_started":
      return "Sync listen started";
    case "sync_stopped":
      return "Sync listen paused";
    case "sync_finished":
      return "Sync listen finished";
    case "song_changed":
      const metadata = message.metadata as { songTitle?: string } | null;
      return `Now playing: ${metadata?.songTitle || "Unknown"}`;
    default:
      return message.serverMessageType || "System message";
  }
}

export function SyncListenChat({
  compoId,
  syncedToDiscordChannel,
  isAdmin,
  onSetDiscordSync,
}: SyncListenChatProps): JSX.Element {
  const { user: userState } = useUserStatus();
  const isLoggedIn = userState.type === "logged-in";

  const [messages, setMessages] = useState<SyncListenChatMessageJSON[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [inputValue, setInputValue] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [dismissedOverlay, setDismissedOverlay] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const lastMessageIdRef = useRef<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Scroll to bottom
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  // Fetch messages
  const fetchMessages = useCallback(async () => {
    const result = await api.get<"/api/compo/:id/sync/chat">(
      `/api/compo/${compoId}/sync/chat`
    );

    if (!api.isToast(result) && result.messages) {
      setMessages(result.messages);
      if (result.messages.length > 0) {
        const newLastId = result.messages[result.messages.length - 1].id;
        if (lastMessageIdRef.current !== newLastId) {
          lastMessageIdRef.current = newLastId;
          // Scroll to bottom when new messages arrive
          setTimeout(scrollToBottom, 100);
        }
      }
    }
    setIsLoading(false);
  }, [compoId, scrollToBottom]);

  // Initial fetch
  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  // Poll for new messages every 3 seconds
  useEffect(() => {
    const interval = setInterval(fetchMessages, 3000);
    return () => clearInterval(interval);
  }, [fetchMessages]);

  // Send message
  const sendMessage = async () => {
    if (!inputValue.trim() || isSending) return;

    setIsSending(true);
    const result = await api.post<"/api/compo/:id/sync/chat/send">(
      `/api/compo/${compoId}/sync/chat/send`,
      { content: inputValue.trim() }
    );

    if (!api.isToast(result)) {
      setMessages((prev) => [...prev, result.message]);
      setInputValue("");
      setTimeout(scrollToBottom, 100);
    }
    setIsSending(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <div className="text-sm text-slate-500 dark:text-slate-400">
          Loading chat...
        </div>
      </div>
    );
  }

  const showDiscordSyncOverlay = !syncedToDiscordChannel && !dismissedOverlay;
  const canSendMessages = syncedToDiscordChannel;

  return (
    <div className="relative flex flex-1 flex-col overflow-hidden">
      {/* Discord sync overlay */}
      {showDiscordSyncOverlay && (
        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-slate-900/80 backdrop-blur-sm">
          <p className="mb-4 text-center text-sm text-white">
            Chat not currently synced to Discord
          </p>
          <div className="flex gap-2">
            {isAdmin && (
              <Button variant="primary" onClick={onSetDiscordSync} grow={false}>
                Connect to Discord
              </Button>
            )}
            <Button
              variant="default"
              onClick={() => setDismissedOverlay(true)}
              grow={false}
            >
              See anyway
            </Button>
          </div>
        </div>
      )}

      {/* Messages */}
      <div ref={messagesContainerRef} className="flex-1 overflow-y-auto">
        {messages.length === 0 ? (
          <div className="flex h-full items-center justify-center">
            <div className="px-4 text-center">
              <ChatBubbleLeftRightIcon className="mx-auto mb-3 h-10 w-10 text-slate-300 dark:text-slate-600" />
              <p className="text-sm text-slate-500 dark:text-slate-400">
                No messages yet
              </p>
            </div>
          </div>
        ) : (
          <div className="py-2">
            {messages.map((message) => (
              <ChatMessage key={message.id} message={message} />
            ))}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Input */}
      {isLoggedIn ? (
        <div className="border-t border-slate-200 p-3 dark:border-slate-700">
          <div className="flex gap-2">
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={canSendMessages ? handleKeyDown : undefined}
              placeholder={
                canSendMessages ? "Type a message..." : "Chat disabled"
              }
              maxLength={500}
              disabled={!canSendMessages}
              className="flex-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 placeholder-slate-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-600 dark:bg-slate-700 dark:text-white dark:placeholder-slate-400"
            />
            <button
              onClick={sendMessage}
              disabled={!inputValue.trim() || isSending || !canSendMessages}
              className="flex items-center justify-center rounded-lg bg-blue-500 px-3 py-2 text-white transition hover:bg-blue-600 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <PaperAirplaneIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      ) : (
        <div className="border-t border-slate-200 p-3 dark:border-slate-700">
          <p className="text-center text-sm text-slate-500 dark:text-slate-400">
            <RLink to="/login" className="text-blue-500 hover:underline">
              Log in
            </RLink>{" "}
            to chat
          </p>
        </div>
      )}
    </div>
  );
}
